import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { RocketIcon, ChevronDown } from 'lucide-react';
import '@r/assets/styles/animation.css';

import { cn } from 'ressources/src/utils/utils';
import { useTranslation } from 'react-i18next';
import pointsTranslation from '@r/translations/points-translation.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import type { LogicTypeStepWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';

type Step = LogicTypeStepWorkspaceReadModel;

interface ProgressProps extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  stepAccomplishments: number[];
  steps?: Step[];
  currentPoints?: number;
}

const Progress = forwardRef<ComponentRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  ({ className, value = 0, steps, stepAccomplishments = [], currentPoints = 0, ...props }, ref) => {
    const { t } = useTranslation();

    if (!value && stepAccomplishments) {
      const sumOfCompletedAccomplishments = stepAccomplishments.reduce(
        (sum, accomplishment) => sum + accomplishment,
        0
      );

      const totalProgress = sumOfCompletedAccomplishments / (stepAccomplishments.length * 100);

      value = totalProgress * 100;
    }

    // Crée une étiquette pour l'indicateur en arrondissant la valeur à l'entier le plus proche et en ajoutant un signe de pourcentage
    // const indicatorLabel = Math.floor(value) + '%'.

    const getProgressStepLabel = (steps: Step[], index: number) => {
      let stepPoints: Step['stepPoint'] = 0;

      if (index !== 0) {
        stepPoints = steps[index - 1].stepPoint || 0;
      }

      return pointsTranslation(t, stepPoints);
    };
    const shouldDisplayTooltip = currentPoints > 1;
    const progressBar = (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn('overflow-hidden relative w-full h-3 rounded-full bg-secondary', className)}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="relative flex-1 w-full h-full rounded-full transition-all bg-primary"
          style={{
            transform: `translateX(-${100 - (value || 0)}%)`,
            animation: 'full-width-basic 2s ease-out forwards'
          }}
        />
      </ProgressPrimitive.Root>
    );

    return (
      <div className="px-5 pt-[3.6rem] pb-[2.5rem] sm:px-16">
        <div className="relative">
          <div
            className="relative flex-1 w-full h-full transition-all"
            style={{
              transform: `translateX(-${100 - (value || 0)}%)`,
              animation: 'full-width-basic 2s ease-out forwards'
            }}
          >
            <div className="absolute top-[-4rem] right-0 flex translate-x-[50%] flex-nowrap items-center whitespace-nowrap rounded-md bg-primary px-3 py-2 text-sm xl:flex">
              <div className="flex justify-center items-center w-6 rounded-full border aspect-square border-foreground">
                <RocketIcon size={13} />
              </div>
              <span className="hidden sm:ml-2 sm:block">{t('logic.step.cursor.label')}</span>
              <div className="absolute bottom-[-1.2rem] left-0 w-full justify-center flex">
                <ChevronDown className="stroke-primary" size={20} />
              </div>
            </div>
          </div>

          {Array.from({ length: stepAccomplishments.length + 1 }).map((_, index) => (
            <div
              key={index}
              className="absolute top-0 z-40 box-border flex aspect-square h-full translate-x-[-50%] items-center justify-center rounded-full bg-foreground"
              style={{ left: `${(index / stepAccomplishments.length) * 100}%` }}
            >
              <div className="w-1 h-1 rounded-full bg-background" />

              {steps && (
                <div className="absolute bottom-[-2.3rem] flex text-foreground md:bottom-[-3.5rem] md:flex-col lg:bottom-[-2.3rem] lg:flex-row xl:bottom-[-3.5rem] xl:flex-col 2xl:bottom-[-2.3rem] 2xl:flex-row">
                  {index === 0
                    ? 0
                    : `${FormattedNumber({ value: steps[index - 1].stepPoint ?? 0, style: 'decimal' })} `}
                  <span className={`ml-2 hidden${steps.length <= 4 ? ' md:block' : ''}`}>
                    {getProgressStepLabel(steps, index)}
                  </span>
                </div>
              )}
            </div>
          ))}

          {shouldDisplayTooltip ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>{progressBar}</div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="flex gap-2 justify-center items-center whitespace-nowrap">
                    <span className="w-3 h-3 rounded-sm bg-primary" />
                    <span>
                      <span className="font-bold">
                        <FormattedNumber
                          value={currentPoints}
                          style="decimal"
                          suffix={` ${pointsTranslation(t, currentPoints, false)}`}
                        />
                      </span>{' '}
                      {t('logic.progress.tooltip.generated.company')}
                    </span>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            progressBar
          )}
        </div>
      </div>
    );
  }
);

Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
